/**
 * Which table columns turn themselves into deep links, and which hrefs are safe.
 *
 * The assistant is told never to write link syntax inside a table cell, because
 * deciding it here is more reliable: the id columns become clickable whatever the
 * model wrote.
 *
 * Two guards:
 *
 *  - **All or none.** Every non-empty value in the column must look like a valid id.
 *    Otherwise a column containing "n/a" and "—" becomes a wall of links to nowhere,
 *    which is worse than plain text.
 *  - **Re-check each cell.** The column decision says "this column is linkable"; the
 *    per-cell check keeps one stray value as plain text.
 *
 * A column whose id has no page in this SPA is deliberately absent from the table
 * below — an external reference must stay text, never get a guessed route.
 */

export type LinkKind = 'application' | 'doctrine' | 'provider';

/** Ids the portal actually has a detail route for. Keep in sync with `App.tsx`. */
const ID_VALUE = /^[A-Za-z0-9][A-Za-z0-9_-]{2,63}$/;

const COLUMNS: { kind: LinkKind; header: RegExp; value: RegExp; path: (id: string) => string }[] = [
  {
    kind: 'application',
    header: /^(application|app|loan)(\s*(id|#|number))?$/i,
    value: ID_VALUE,
    path: (id) => `/applications/${encodeURIComponent(id)}`,
  },
  {
    kind: 'doctrine',
    header: /^doctrine(\s*id)?$/i,
    value: ID_VALUE,
    path: (id) => `/doctrine-config/${encodeURIComponent(id)}`,
  },
  {
    kind: 'provider',
    header: /^provider(\s*id)?$/i,
    value: ID_VALUE,
    path: (id) => `/providers/${encodeURIComponent(id)}`,
  },
];

/** The link kind for a whole column, or `null` to leave it as text. */
export function linkKindForHeader(header: string, values: string[]): LinkKind | null {
  const rule = COLUMNS.find((c) => c.header.test(String(header ?? '').trim()));
  if (!rule) return null;
  const present = values.map((v) => String(v ?? '').trim()).filter(Boolean);
  if (!present.length) return null;
  return present.every((v) => rule.value.test(v)) ? rule.kind : null;
}

/** The SPA path for one cell, or `null` if this particular value is not a valid id. */
export function pathForCell(kind: LinkKind, raw: string): string | null {
  const value = String(raw ?? '').trim();
  const rule = COLUMNS.find((c) => c.kind === kind);
  if (!rule || !rule.value.test(value)) return null;
  return rule.path(value);
}

export type ResolvedHref =
  | { kind: 'internal'; path: string }
  | { kind: 'external'; url: string }
  | null;

/**
 * Link policy for model-authored hrefs.
 *
 * Anything not provably safe renders as plain text — the label survives, the anchor
 * does not. In particular `javascript:`, `data:`, `vbscript:` and protocol-relative
 * `//host` are rejected outright, and a bare relative path is rejected because it
 * would resolve against whatever route the operator happens to be on.
 *
 * An absolute portal URL that points at an application detail page is folded back to
 * an internal path, so `admin_url` values from the backend stay inside the SPA.
 */
export function resolveHref(href: string): ResolvedHref {
  const raw = String(href ?? '').trim();
  if (!raw) return null;
  if (raw.startsWith('//')) return null;
  if (raw.startsWith('/')) return { kind: 'internal', path: raw };

  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(raw);
  if (!scheme) return null;
  /*
   * `https` only — not `https?`.
   *
   * The policy is that a reply may render exactly two things as links: an in-app `/path`
   * and an `https:` URL. Plain `http:` was slipping through, which is a downgrade an
   * assistant reply should never offer: no tool returns a URL at all, so an `http:` link
   * in a reply is either a hallucination or something injected into the model's context.
   * Rendering it as text keeps the label visible and the anchor absent.
   */
  if (!/^https$/i.test(scheme[1])) return null;

  try {
    const url = new URL(raw);
    if (/\/applications\//.test(url.pathname)) {
      return { kind: 'internal', path: `${url.pathname}${url.search}` };
    }
    return { kind: 'external', url: raw };
  } catch {
    return null;
  }
}
